import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { SkuCatalogEntry, SkuCatalogState } from "./use_sku_catalog";
import { addSkuPoKey } from "./get_matrix";

/** The PO an added line lands on. Supplier + ship-to + both dates is the grain
 * the PDF groups on, so this identifies exactly one purchase order. */
export type AddSkuTarget = {
  supplier: string;
  location: string;
  orderPlacedDate: string;
  orderDeliveryDate: string;
};

export type AddSkuLine = {
  sku: string;
  skuName: string;
  supplier: string;
  location: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
};

const formatDate = (value: string): string => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const formatCurrency = (n: number): string =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export const AddSkuDialog = ({
  open,
  onClose,
  onConfirm,
  catalog,
  existingSkusByPo,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (line: AddSkuLine, target: AddSkuTarget) => void;
  catalog: SkuCatalogState;
  /** SKUs already on a PO, keyed `supplier|location|orderDay` — offering one
   * again would collide with the line that is already there. */
  existingSkusByPo: Record<string, string[]>;
}) => {
  const theme = useTheme();
  const [supplier, setSupplier] = React.useState<string | null>(null);
  const [location, setLocation] = React.useState<string | null>(null);
  // The inbox shows two order weeks, so an added line has to say which one.
  const [orderDate, setOrderDate] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<SkuCatalogEntry | null>(null);
  const [quantity, setQuantity] = React.useState("");
  const [unitCost, setUnitCost] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setSupplier(null);
      setLocation(null);
      setOrderDate(null);
      setSelected(null);
      setQuantity("");
      setUnitCost("");
    }
  }, [open]);

  const suppliers = React.useMemo(
    () => [...new Set(catalog.entries.map((e) => e.supplier))].sort(),
    [catalog.entries]
  );

  // Only the locations this supplier actually ships to, so an invalid pair
  // cannot be picked. Same idea for the SKU list below.
  const locations = React.useMemo(
    () =>
      supplier
        ? [
            ...new Set(
              catalog.entries
                .filter((e) => e.supplier === supplier)
                .map((e) => e.location)
            ),
          ].sort()
        : [],
    [catalog.entries, supplier]
  );

  // Distinct order cycles for this lane — usually the two weeks the inbox is
  // showing, each with its own delivery date.
  const cycles = React.useMemo(() => {
    if (!supplier || !location) return [];
    const seen = new Map<string, { orderDate: string; deliveryDate: string }>();
    for (const e of catalog.entries) {
      if (e.supplier !== supplier || e.location !== location) continue;
      // cellToDate yields "" for a null order_date. Kept, it sorts ahead of
      // every real week, wins the default-cycle effect below, and then reads as
      // falsy — so the SKU list stays empty and its helper text still says to
      // choose a supplier and location, which the planner just did.
      if (!e.orderDate) continue;
      if (!seen.has(e.orderDate)) {
        seen.set(e.orderDate, {
          orderDate: e.orderDate,
          deliveryDate: e.deliveryDate,
        });
      }
    }
    return [...seen.values()].sort((a, b) =>
      a.orderDate.localeCompare(b.orderDate)
    );
  }, [catalog.entries, supplier, location]);

  // Default to the nearest cycle; that is the order being placed now. Keep an
  // existing choice if it is still on offer — the catalog subscription emits
  // more than once (cached, then fresh), and resetting unconditionally would
  // silently pull the planner back to week one after they picked week two.
  React.useEffect(() => {
    setOrderDate((current) =>
      current && cycles.some((c) => c.orderDate === current)
        ? current
        : (cycles[0]?.orderDate ?? null)
    );
  }, [cycles]);

  // Normalised on both sides. addSkuPoKey trims and lowercases the LANE for a
  // reason — the two sides come from different datasets — and the SKU code needs
  // the same treatment: "ABC-123" in approved_pos against "abc-123" in the
  // master would offer a SKU already on the PO, and the resolver's own
  // duplicate backstop compares verbatim too, so nothing else would catch it.
  // orderDate is reconciled against a refreshed catalog above; `selected` has to
  // be too. Otherwise the fresh emit moves the cycle, the SKU is no longer on
  // offer, and the field still shows it with Add enabled — confirming would
  // write a line dated to a cycle the plan no longer has.
  const alreadyOnPo = React.useMemo(
    () =>
      new Set(
        (
          existingSkusByPo[
            addSkuPoKey(
              supplier ?? "",
              location ?? "",
              (orderDate ?? "").slice(0, 10)
            )
          ] ?? []
        ).map((sku) => sku.trim().toLowerCase())
      ),
    [existingSkusByPo, supplier, location, orderDate]
  );

  const skus = React.useMemo(
    () =>
      supplier && location && orderDate
        ? catalog.entries.filter(
            (e) =>
              e.supplier === supplier &&
              e.location === location &&
              e.orderDate === orderDate &&
              !alreadyOnPo.has(e.sku.trim().toLowerCase())
          )
        : [],
    [catalog.entries, supplier, location, orderDate, alreadyOnPo]
  );

  React.useEffect(() => {
    setSelected((current) => {
      if (!current) return current;
      const stillOffered = skus.some(
        (e) => e.sku === current.sku && e.orderDate === current.orderDate
      );
      if (stillOffered) return current;
      setUnitCost("");
      return null;
    });
  }, [skus]);

  const handleSelect = (entry: SkuCatalogEntry | null) => {
    setSelected(entry);
    // Prefill from the master so the common case is type-a-quantity-and-go.
    setUnitCost(
      entry?.cost !== null && entry?.cost !== undefined ? String(entry.cost) : ""
    );
  };

  const parsedQuantity = Number(quantity.replace(/,/g, ""));
  const parsedUnitCost = Number(unitCost.replace(/,/g, ""));
  // Whole units only: quantity is eaches on the PO line and reaches the vendor
  // document as typed, so "1.5" would print a fractional case count and round
  // differently in the export than on screen.
  const quantityValid =
    quantity !== "" && Number.isInteger(parsedQuantity) && parsedQuantity > 0;
  const unitCostValid =
    unitCost !== "" && Number.isFinite(parsedUnitCost) && parsedUnitCost >= 0;
  const canConfirm = !!selected && quantityValid && unitCostValid;

  const cases =
    selected?.qtyPerCase && selected.qtyPerCase > 0 && quantityValid
      ? parsedQuantity / selected.qtyPerCase
      : null;
  // A warning, not a block: a planner may knowingly order under the minimum.
  const belowMoq =
    selected?.moq != null && quantityValid && parsedQuantity < selected.moq;
  // Also a warning, not a block. Zero is usually a mistake on a purchase line,
  // but a planner may legitimately raise one for a free or sample shipment, and
  // the cost is informational on the PO — refusing it would be the wrong call.
  const zeroCost = unitCostValid && parsedUnitCost === 0;

  const handleConfirm = () => {
    if (!canConfirm || !selected) return;
    onConfirm(
      {
        sku: selected.sku,
        skuName: selected.skuName,
        supplier: selected.supplier,
        location: selected.location,
        quantity: parsedQuantity,
        unitCost: parsedUnitCost,
        // Rounded like the grid's own quantity/cost interlock: this lands in
        // approved_pos and then the CSV and PDF, and 3 x 19.99 stores
        // 59.970000000000006 while the dialog above reads $59.97.
        totalCost: Math.round(parsedQuantity * parsedUnitCost * 100) / 100,
      },
      {
        supplier: selected.supplier,
        location: selected.location,
        // Straight from the cycle the inbox is showing — never typed, never
        // derived here — so an added line cannot land in a different cycle.
        orderPlacedDate: selected.orderDate,
        orderDeliveryDate: selected.deliveryDate,
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>Add SKU to PO</Box>
        <IconButton
          onClick={onClose}
          sx={{ p: 0, color: theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {catalog.loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading SKUs…</Typography>
          </Box>
        )}

        {catalog.error && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <Typography variant="body2" color="error">
              {catalog.error}
            </Typography>
            <Button size="small" onClick={catalog.retry}>
              Retry
            </Button>
          </Box>
        )}

        {!catalog.loading && !catalog.error && (
          <>
            <Autocomplete
              options={suppliers}
              value={supplier}
              onChange={(_, v) => {
                setSupplier(v);
                setLocation(null);
                handleSelect(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Supplier"
                  variant="standard"
                  margin="normal"
                  helperText={`${suppliers.length} suppliers`}
                />
              )}
            />

            <Autocomplete
              options={locations}
              value={location}
              disabled={!supplier}
              onChange={(_, v) => {
                setLocation(v);
                handleSelect(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  variant="standard"
                  margin="normal"
                  helperText={
                    supplier
                      ? `${locations.length} ship-to locations for this supplier`
                      : "Choose a supplier first"
                  }
                />
              )}
            />

            {cycles.length > 1 && (
              <Autocomplete
                options={cycles.map((c) => c.orderDate)}
                value={orderDate}
                disabled={!supplier || !location}
                onChange={(_, v) => {
                  setOrderDate(v);
                  handleSelect(null);
                }}
                getOptionLabel={(o) => {
                  const c = cycles.find((x) => x.orderDate === o);
                  return c
                    ? `Order ${formatDate(c.orderDate)} · ETA ${formatDate(c.deliveryDate)}`
                    : formatDate(o);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Order week"
                    variant="standard"
                    margin="normal"
                    helperText="The inbox shows two order weeks"
                  />
                )}
              />
            )}

            <Autocomplete
              options={skus}
              value={selected}
              disabled={!supplier || !location || !orderDate}
              onChange={(_, v) => handleSelect(v)}
              getOptionLabel={(o) =>
                o.skuName ? `${o.sku} — ${o.skuName}` : o.sku
              }
              isOptionEqualToValue={(a, b) => a.sku === b.sku}
              noOptionsText="No SKUs left to add for this supplier and location"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="SKU"
                  variant="standard"
                  margin="normal"
                  helperText={
                    supplier && location
                      ? `${skus.length} available`
                      : "Choose a supplier and location first"
                  }
                />
              )}
            />

            {selected && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Order {formatDate(selected.orderDate)} · ETA{" "}
                  {formatDate(selected.deliveryDate)}
                  {selected.moq != null
                    ? ` · MOQ ${selected.moq.toLocaleString("en-US")}`
                    : ""}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <TextField
                label="Quantity (units)"
                variant="standard"
                margin="normal"
                fullWidth
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={quantity !== "" && !quantityValid}
                helperText={
                  quantity !== "" && !quantityValid
                    ? "Enter a whole quantity greater than zero"
                    : belowMoq
                      ? `Below the ${selected?.moq?.toLocaleString("en-US")} minimum`
                      : cases !== null
                        ? `${cases.toLocaleString("en-US", { maximumFractionDigits: 2 })} cases`
                        : " "
                }
              />
              <TextField
                label="Unit cost"
                variant="standard"
                margin="normal"
                fullWidth
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                error={unitCost !== "" && !unitCostValid}
                helperText={
                  unitCost !== "" && !unitCostValid
                    ? "Enter a valid cost"
                    : zeroCost
                      ? "Zero cost — this line will total $0"
                      : " "
                }
              />
            </Box>

            <Typography variant="body2" sx={{ mt: 1 }}>
              Total:{" "}
              <strong>
                {canConfirm
                  ? formatCurrency(parsedQuantity * parsedUnitCost)
                  : "—"}
              </strong>
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              Added lines are approved immediately and appear here, then flow
              to the PO PDF and the approved-POs export. Un-ticking one deletes
              it — unlike a suggested line, it has no Needs Review row to
              return to.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleConfirm}
          variant="contained"
          // The form hides while the catalog reloads, but this button lives
          // outside that guard — leaving it live would submit the stale
          // selection against the new run's cycle.
          disabled={!canConfirm || catalog.loading || !!catalog.error}
        >
          Add to PO
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
